import React, { useContext, useState, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CCard, CDataTable, CBadge, CCollapse, CTooltip, CImg} from '@coreui/react';
import { useSelector } from 'react-redux';
import { Job, JobPhaseColor, JobPhaseLabel } from '../../shared/models/jobs';
import tooltip from '../../assets/images/tool_tip.svg';
import { UserInfo } from '../../shared/models/user';
import JobActions from '../../jobs/components/JobActions';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import TableStats from '../../shared/components/TableStats';
import JobsDetailsContainer from './JobsDetailsContainer';
import { selectOrgList } from '../../store/selectors/orgHierarchy.selectors';
import { formatJobDescription } from '../../shared/utils/job-utils';

export const COLUMN_SIZES = {
  appointmentID: 6,
  id: 6,
  createdTime: 6,
  startedTime: 6,
  customerName: 6,
  apptTime: 6,
  promisedTime: 6,
  completedTime: 6,
  phones: 6,
  destination: 6,
  arrivalTime: 6,
  completedReasonCode: 6,
  timeOnSite: 6,
  emails: 6,
  waitTime: 6,
  distanceTraveled: 6,
  description: 12,
  feedbackRating: 12,
  images: 12,
};

const FIELDS = (labels, enableOrgHierarchyModule, isSessionsEnabled) => [
  {
    key: 'technician',
    label: labels.agent,
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'phase',
    label: 'Status',
  },
  ...(enableOrgHierarchyModule ? [{
    key: 'org_name',
    label: 'Org Name',
    filter: false,
    sorter: false,
  }] : []),
  ...(isSessionsEnabled ? [{
    key: 'session_id',
    label: 'Session ID',
  }, {
    key: 'rank',
    label: 'Session Rank',
    sorter: false,
    filter: false,
  }] : []),
  {
    key: 'actions',
    label: '',
    sorter: false,
    filter: false,
  },
];

export interface ActiveJobListProps {
  title: string;
  jobs: Job[];
  technicians: UserInfo[];
  loading: boolean;
  isSessionsEnabled?: boolean;
}

const JobList = (props: ActiveJobListProps) => {
  const { title, jobs, technicians, loading, isSessionsEnabled = false } = props;
  const {
    enableOrgHierarchyModule
  } = useFlags();
  const orgs = useSelector(selectOrgList);
  const labels = useContext(ConfigContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState<string>(undefined);

  const handleRowClick = ({ invite_code: inviteCode }: Job, index, columnName, e) => {
    if (columnName !== 'actions' || e.target.tagName.toLowerCase() === 'td') {
      setShowDrawer(showDrawer !== inviteCode ? inviteCode : undefined);
    }
  };



  const formattedJobs = useMemo(() => {
    return jobs?.map((job) => {
      let name = 'Unassigned';
      if (job?.agent_id !== -1) {
        name = technicians?.find(tech => tech.id === job?.agent_id)?.displayName || 'Unassigned';
      }
      return {
        ...job,
        org_name: orgs?.find(value => value?._id === job?.org_id)?.name ?? '',
        technician: name,
      }
    })?.sort((a, b) => {
      if (a.session_id === b.session_id) {
        return a.rank > b.rank ? 1 : -1;
      }
      return 0;
    });
  }, [jobs, technicians]);

  return (
    <div>
      <div className='glympser-cardHeader'>
        <div className='glympser-cardTitle'>
          {title}
        </div>
        <CTooltip placement="top" content="Orders with an appointment start date on or before today(last 3 days)">
          <CImg src={tooltip} className="glympser-toolTip"/>
        </CTooltip>
      </div>
      <CCard className="glympser-container--roundedWithShadow">
        <div className='job-list-container'>
          <CDataTable
            fields={ FIELDS(labels, enableOrgHierarchyModule, isSessionsEnabled) }
            items={ formattedJobs }
            loading={ loading }
            clickableRows
            sorter
            noItemsViewSlot={<div className='glympse-noItemsMsg'>No Items</div>}
            responsive
            onRowClick={ handleRowClick }
            pagination={{
              addListClass: "ml-2"
            }}
            onPageChange={(page) => setCurrentPage(page)}
            underTableSlot={formattedJobs && formattedJobs.length > 0 && <TableStats page={currentPage} total={formattedJobs.length} />}
            scopedSlots={{
              // eslint-disable-next-line react/no-unused-prop-types
              'technician': ({ technician }: Job & {technician: string}) => {
                return <td>{ technician }</td>;
              },
              'phase': ({ phase }: Job) => {
                return (
                  <td style={{ width: '1%' }}>
                    <CBadge color={JobPhaseColor[phase] || 'secondary'}>{JobPhaseLabel[phase] || 'Unknown'}</CBadge>
                  </td>
                );
              },
              'description': (job: Job) => (
                <td>
                  <div className="line-clamp">
                    {formatJobDescription(job.description, job?.foreign_id, isSessionsEnabled)}
                  </div>
                </td>
              ),
              'actions': (job: Job) => {
                return (
                  <td style={{ width: '1%' }}>
                    <JobActions job={ job } />
                  </td>
                );
              },
              'rank': (job: Job) => {
                return (
                  <td>{job?.rank >= 0 ? job?.rank : '-'}</td>
                );
              },
              'details': (job: Job) => {
                if (showDrawer !== job?.invite_code) {
                  return null;
                }

                return (
                  <CCollapse show>
                    <JobsDetailsContainer job={job} columnSizes={COLUMN_SIZES} />
                  </CCollapse>
                );
              },
            }}
          />
        </div>
      </CCard>
    </div>
  );
};

export default JobList;
